/**
 * Display / checkout rules for API subscription plan objects (offer_price = 0 means unset; free days flags).
 */

export type PlanLikeForPricing = {
  cost?: number | null;
  offer_cost?: number | null;
};

/**
 * A real offer only when backend sent a **positive** offer below list price. `offer_cost` 0/undefined = no promo (use `cost`).
 */
export function hasActiveSubscriptionOffer(plan: PlanLikeForPricing): boolean {
  const cost = Number(plan?.cost);
  const offer = plan?.offer_cost;
  if (offer == null) {
    return false;
  }
  const o = Number(offer);
  if (!Number.isFinite(o) || o <= 0) {
    return false;
  }
  if (!Number.isFinite(cost) || cost <= 0) {
    return o > 0;
  }
  return o < cost;
}

export function getPlanDisplayPrice(plan: PlanLikeForPricing): number {
  if (hasActiveSubscriptionOffer(plan)) {
    return Number(plan.offer_cost);
  }
  return Number(plan?.cost ?? 0);
}

export type PlanLikeForFreeDays = {
  free_days?: number | null;
  start_day_limit?: number | null;
  /** `false` = do not show optional free-days choice (backend not exposing customer choice; use checkout without add-on). */
  is_free_days_for_customer?: boolean | null;
};

/**
 * When to show the “add free days?” sheet before checkout. Requires promo days + start window; respects `is_free_days_for_customer` when set.
 * If the flag is **omitted** (undefined), keep legacy behavior (treat as eligible) so older payloads still get the modal when appropriate.
 */
export function shouldOfferFreeDaysModal(plan: PlanLikeForFreeDays): boolean {
  const n = Number(plan?.free_days ?? 0);
  if (!Number.isFinite(n) || n <= 0) {
    return false;
  }
  if (Number(plan?.start_day_limit ?? 0) <= 0) {
    return false;
  }
  if (plan?.is_free_days_for_customer === false) {
    return false;
  }
  return true;
}
